import './DistributionFormComponent.scss';
import { useEffect, useState } from 'react';
import { BrandInfo, IProfileState, IRegion } from '../../../../redux/AppReducer';
import { ActionsForms } from '../../GenericBaseForms/ActionsForms/ActionsForms';
import { ContainerForms } from '../../GenericBaseForms/ContainerForms/ContainerForms';
import TitleForms from '../../GenericBaseForms/TitleForms/TitleForms';
import LabelForms from '../../GenericBaseForms/LabelForms/LabelForms';
import React from 'react';
import { useFormik, FormikErrors } from 'formik';
import * as Yup from 'yup';
import CheckboxFormComponent from '../../GenericBaseForms/CheckboxFormComponent/CheckboxFormComponent';
import { DEFAULT_LABELS } from './model';
import LoadingBobComponent from '../../GenericBaseForms/LoadingBobComponent/LoadingBobComponent';
import { formatErrors } from '../model';
interface IDistributionForm {
	brandState: BrandInfo;
	onUpdateProfileBrand: (brandState: BrandInfo) => void;
	isEditing: string | null;
	toggleEditing: (id: string | null) => void;
	onCancel: () => void;
	onChange: (data: any) => void;
	onValidationChange: (isValid: boolean) => void;
	updateKeyFc: () => void;
	profileState: IProfileState;
	setErrors: (errors: any) => void;
}

type DistributorRegionKey =
	| 'keheRegion'
	| 'UNFIRegion'
	| 'otherDistributorRegion'
	| 'DotFoodsRegion'
	| 'DPIRegion'
	| 'PodFoodsRegion'
	| 'ThresholdEntRegion';

const DISTRIBUTOR_CONFIG: Array<{
	id: string;
	regionKey: DistributorRegionKey;
	labelConstant: keyof typeof DEFAULT_LABELS;
}> = [
	{ id: 'KeHe', regionKey: 'keheRegion', labelConstant: 'keheRegion' },
	{ id: 'UNFI', regionKey: 'UNFIRegion', labelConstant: 'UNFIRegion' },
	{ id: 'Other', regionKey: 'otherDistributorRegion', labelConstant: 'otherDistributorRegion' },
	{ id: 'DotFoods', regionKey: 'DotFoodsRegion', labelConstant: 'DotFoodsRegion' },
	{ id: 'DPI', regionKey: 'DPIRegion', labelConstant: 'DPIRegion' },
	{ id: 'PodFoods', regionKey: 'PodFoodsRegion', labelConstant: 'PodFoodsRegion' },
	{ id: 'ThresholdEnt', regionKey: 'ThresholdEntRegion', labelConstant: 'ThresholdEntRegion' }
];

const DistributionSchema = Yup.object().shape({
	mapPolicy: Yup.boolean(),
	currentDistributionChannels: Yup.array(),
	currentRetailChannels: Yup.array(),
	...DISTRIBUTOR_CONFIG.reduce((acc, config) => {
		acc[config.regionKey] = Yup.array();
		return acc;
	}, {} as Record<DistributorRegionKey, Yup.AnySchema>)
});

interface DistributorRegionsProps {
	isEditing: boolean;
	label: string;
	regionsToDisplay: IRegion[] | undefined;
	namePrefix: DistributorRegionKey;
	handleChange: (event: React.ChangeEvent<any>) => void;
	toggleEditing: (id: string | null) => void;
	formId: string;
	error?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
}

const DistributorRegionCheckboxes: React.FC<DistributorRegionsProps> = React.memo(
	({
		isEditing,
		label,
		regionsToDisplay,
		namePrefix,
		handleChange,
		toggleEditing,
		formId,
		error
	}) => {
		if (!regionsToDisplay || regionsToDisplay.length === 0) {
			return null;
		}
		return (
			<div>
				<LabelForms text={label} />
				{regionsToDisplay.map((region, index) => (
					<CheckboxFormComponent
						key={region.id}
						checkboxType='square'
						name={isEditing ? `${namePrefix}[${index}].isActive` : `${namePrefix}.${region.id}`}
						checkboxText={<>{region.name}</>}
						value={region.isActive!}
						disabled={!isEditing}
						onChange={isEditing ? handleChange : undefined}
						onClick={!isEditing ? () => toggleEditing(formId) : undefined}
					/>
				))}
				{isEditing && error && (
					<p className='error-message'>{Array.isArray(error) ? error.join(', ') : String(error)}</p>
				)}
			</div>
		);
	}
);

export const DistributionFormComponent: React.FC<IDistributionForm> = ({
	brandState,
	onUpdateProfileBrand,
	onCancel,
	isEditing,
	toggleEditing,
	onChange,
	onValidationChange,
	updateKeyFc,
	profileState,
	setErrors: setFormErrors
}) => {
	const ID = 'distribution-channels';
	const [isVisible, setVisibility] = useState<boolean>(true);
	const [showRetails, setShowRetails] = useState<boolean>(true);
	const [showDistributorRegions, setShowDistributorRegions] = useState<Record<string, boolean>>(
		() =>
			DISTRIBUTOR_CONFIG.reduce((acc, dist) => {
				acc[dist.id] =
					brandState.currentDistributionChannels?.find(c => c.id === dist.id)?.isActive || false;
				return acc;
			}, {} as Record<string, boolean>)
	);

	const toggleVisibility = () => setVisibility(!isVisible);

	const formik = useFormik({
		initialValues: {
			...brandState,
			mapPolicy: brandState.mapPolicy ?? false,
			currentDistributionChannels: brandState.currentDistributionChannels || [],
			currentRetailChannels: brandState.currentRetailChannels || [],
			...DISTRIBUTOR_CONFIG.reduce((acc, config) => {
				acc[config.regionKey] = brandState[config.regionKey] || [];
				return acc;
			}, {} as Record<DistributorRegionKey, IRegion[]>),
			cleanChannels:
				brandState.currentDistributionChannels?.some(
					c => c.isActive && c.id !== 'NotInDistribution'
				) || false
		},
		onSubmit: submittedValues => {
			onUpdateProfileBrand(submittedValues);
			updateKeyFc();
		},
		validationSchema: DistributionSchema,
		enableReinitialize: true
	});

	const {
		values,
		handleChange,
		handleReset,
		submitForm,
		isValid,
		errors,
		setValues,
		setFieldError
	} = formik;

	const cleanRetailInput = () => {
		const updatedItems = values.currentRetailChannels!.map(item => ({
			...item,
			isActive: false
		}));
		if (JSON.stringify(values.currentRetailChannels) !== JSON.stringify(updatedItems)) {
			setValues(
				prevValues => ({
					...prevValues,
					currentRetailChannels: updatedItems
				}),
				false
			);
		}
		if (showRetails) setShowRetails(false);
	};

	useEffect(() => {
		if (isEditing === ID) {
			onChange(values);
		}
	}, [values, onChange]);

	useEffect(() => {
		onValidationChange(isValid);
	}, [isValid, onValidationChange]);
	useEffect(() => {
		const currentChannels = values.currentDistributionChannels;
		let valuesChanged = false;
		const nextFormValues = { ...values };

		const newShowStates = DISTRIBUTOR_CONFIG.reduce((acc, config) => {
			acc[config.id] = currentChannels.find(c => c.id === config.id)?.isActive || false;
			return acc;
		}, {} as Record<string, boolean>);

		if (JSON.stringify(newShowStates) !== JSON.stringify(showDistributorRegions)) {
			setShowDistributorRegions(newShowStates);
		}
		const hasActiveSpecificChannels = currentChannels.some(
			item => item.isActive && item.id !== 'NotInDistribution'
		);
		const notInDistributionChannel = currentChannels.find(c => c.id === 'NotInDistribution');
		const isNotInDistributionActive = notInDistributionChannel?.isActive || false;
		let newChannelsArray = [...currentChannels];

		if (!hasActiveSpecificChannels && !isNotInDistributionActive && currentChannels.length > 0) {
			const notInDistributionExists = currentChannels.some(c => c.id === 'NotInDistribution');
			if (notInDistributionExists) {
				newChannelsArray = currentChannels.map(item => ({
					...item,
					isActive: item.id === 'NotInDistribution'
				}));
				if (JSON.stringify(newChannelsArray) !== JSON.stringify(currentChannels)) {
					nextFormValues.currentDistributionChannels = newChannelsArray;
					valuesChanged = true;
				}
			}
		} else if (hasActiveSpecificChannels && isNotInDistributionActive) {
			newChannelsArray = currentChannels.map(item =>
				item.id === 'NotInDistribution' ? { ...item, isActive: false } : item
			);
			if (JSON.stringify(newChannelsArray) !== JSON.stringify(currentChannels)) {
				nextFormValues.currentDistributionChannels = newChannelsArray;
				valuesChanged = true;
			}
		}

		if (nextFormValues.cleanChannels !== hasActiveSpecificChannels) {
			nextFormValues.cleanChannels = hasActiveSpecificChannels;
			valuesChanged = true;
		}

		if (valuesChanged) {
			setValues(nextFormValues, false);
		}
	}, [values.currentDistributionChannels, values.cleanChannels, setValues, showDistributorRegions]);

	useEffect(() => {
		if (isEditing !== ID) {
			// handleReset();
		} else {
			const initialFormStateFromBrand = {
				...brandState,
				mapPolicy: brandState.mapPolicy ?? false,
				currentDistributionChannels: brandState.currentDistributionChannels || [],
				currentRetailChannels: brandState.currentRetailChannels || [],
				...DISTRIBUTOR_CONFIG.reduce((acc, config) => {
					acc[config.regionKey] = brandState[config.regionKey] || [];
					return acc;
				}, {} as Record<DistributorRegionKey, IRegion[]>),
				cleanChannels:
					brandState.currentDistributionChannels?.some(
						c => c.isActive && c.id !== 'NotInDistribution'
					) || false
			};
			if (JSON.stringify(values) !== JSON.stringify(initialFormStateFromBrand)) {
				setValues(initialFormStateFromBrand, false);
			}
		}
	}, [brandState, isEditing, ID, setValues, handleReset]);

	useEffect(() => {
		const hasActiveRetails = values.currentRetailChannels.some(item => item.isActive);
		if (showRetails !== hasActiveRetails) {
			setShowRetails(hasActiveRetails);
		}
	}, [values.currentRetailChannels, showRetails]);

	useEffect(() => {
		DISTRIBUTOR_CONFIG.forEach(config => {
			const distributorChannel = values.currentDistributionChannels.find(c => c.id === config.id);
			const isDistributorActive = distributorChannel?.isActive;
			const regions = values[config.regionKey] as IRegion[] | undefined;
			const hasActiveRegion = regions?.some(region => region.isActive);
			const errorMessage = 'Please select at least one option.';

			if (isDistributorActive && !hasActiveRegion) {
				if (errors[config.regionKey] !== errorMessage) {
					setFieldError(config.regionKey, errorMessage);
				}
			} else if (errors[config.regionKey] === errorMessage) {
				setFieldError(config.regionKey, undefined);
			}
		});
	}, [
		values.currentDistributionChannels,
		values.keheRegion,
		values.UNFIRegion,
		values.otherDistributorRegion,
		values.DotFoodsRegion,
		values.DPIRegion,
		values.PodFoodsRegion,
		values.ThresholdEntRegion,
		errors,
		setFieldError
	]);

	useEffect(() => {
		if (Object.keys(errors).length > 0 && ID === profileState.sectionId) {
			setFormErrors(formatErrors(errors, DEFAULT_LABELS));
		} else if (Object.keys(errors).length === 0 && ID === profileState.sectionId) {
			setFormErrors({});
		}
	}, [errors, profileState.sectionId, setFormErrors, ID]);

	const onDistributorChannelChange = (distributorId: string) => {
		const currentChannels = values.currentDistributionChannels;
		let updatedChannels = currentChannels.map(item => {
			if (item.id === distributorId) return { ...item, isActive: !item.isActive };
			return item;
		});

		const nextFormValues = { ...values };
		let regionsModified = false;

		if (distributorId === 'NotInDistribution') {
			const isNotInDistributionNowActive = updatedChannels.find(
				c => c.id === 'NotInDistribution'
			)?.isActive;
			if (isNotInDistributionNowActive) {
				updatedChannels = updatedChannels.map(c => ({
					...c,
					isActive: c.id === 'NotInDistribution'
				}));
				DISTRIBUTOR_CONFIG.forEach(config => {
					if (nextFormValues[config.regionKey]?.some((r: IRegion) => r.isActive)) {
						nextFormValues[config.regionKey] = (nextFormValues[config.regionKey] as IRegion[]).map(
							region => ({
								...region,
								isActive: false
							})
						);
						regionsModified = true;
					}
				});
			}
		} else {
			const anySpecificActive = updatedChannels.some(
				c => c.id !== 'NotInDistribution' && c.isActive
			);
			if (anySpecificActive) {
				updatedChannels = updatedChannels.map(c =>
					c.id === 'NotInDistribution' ? { ...c, isActive: false } : c
				);
			}

			const changedChannel = updatedChannels.find(c => c.id === distributorId);
			if (changedChannel && !changedChannel.isActive) {
				const config = DISTRIBUTOR_CONFIG.find(c => c.id === distributorId);
				if (config && nextFormValues[config.regionKey]?.some((r: IRegion) => r.isActive)) {
					nextFormValues[config.regionKey] = (nextFormValues[config.regionKey] as IRegion[]).map(
						region => ({
							...region,
							isActive: false
						})
					);
					regionsModified = true;
				}
			}
		}

		const anySpecificActiveAfterUpdate = updatedChannels.some(
			c => c.id !== 'NotInDistribution' && c.isActive
		);
		if (
			!anySpecificActiveAfterUpdate &&
			!updatedChannels.find(c => c.id === 'NotInDistribution')?.isActive &&
			updatedChannels.some(c => c.id === 'NotInDistribution')
		) {
			updatedChannels = updatedChannels.map(c =>
				c.id === 'NotInDistribution' ? { ...c, isActive: true } : c
			);
		}

		let finalValuesToSet = { ...nextFormValues };
		if (JSON.stringify(currentChannels) !== JSON.stringify(updatedChannels) || regionsModified) {
			finalValuesToSet.currentDistributionChannels = updatedChannels;
			finalValuesToSet.cleanChannels = anySpecificActiveAfterUpdate;
			setValues(finalValuesToSet, false);
		} else if (values.cleanChannels !== anySpecificActiveAfterUpdate) {
			setValues(prev => ({ ...prev, cleanChannels: anySpecificActiveAfterUpdate }), false);
		}
	};
	return (
		<div className='profile-distribution' id={ID}>
			<div className='distribution-tab'>
				<TitleForms
					isEditing={isEditing === ID}
					isVisible={isVisible}
					isValid={isValid}
					onUpdate={submitForm}
					titleText='Distribution / Channels'
					toggleEditing={() => toggleEditing(ID)}
					toggleVisibility={toggleVisibility}
					profileState={profileState}
				/>
				{ID === profileState.sectionId && profileState.loading ? (
					<LoadingBobComponent />
				) : (
					<>
						{isVisible &&
							(isEditing !== ID ? (
								// VIEW MODE
								<ContainerForms
									FormComponent1={
										<>
											<div>
												<LabelForms text={DEFAULT_LABELS.currentDistributionChannels} />
												{(brandState.currentDistributionChannels || [])
													.filter(channel => channel.isActive)
													.map(distributor => (
														<CheckboxFormComponent
															checkboxType='square'
															key={'view-distributor-' + distributor.id}
															name={`currentDistributionChannels.view.${distributor.id}`}
															checkboxText={<>{distributor.name}</>}
															value={distributor.isActive}
															disabled={true}
															onClick={() => toggleEditing(ID)}
														/>
													))}
											</div>
											{DISTRIBUTOR_CONFIG.map(
												config =>
													brandState.currentDistributionChannels?.find(c => c.id === config.id)
														?.isActive && (
														<DistributorRegionCheckboxes
															key={`view-regions-${config.id}`}
															isEditing={false}
															label={DEFAULT_LABELS[config.labelConstant]}
															regionsToDisplay={brandState[config.regionKey]?.filter(
																r => r.isActive
															)}
															namePrefix={config.regionKey}
															handleChange={handleChange} // Not used in view mode by CheckboxFormComponent
															toggleEditing={toggleEditing}
															formId={ID}
														/>
													)
											)}
										</>
									}
									FormComponent2={
										<>
											<div>
												<LabelForms text={DEFAULT_LABELS.currentRetailChannels} />
												{brandState.currentRetailChannels?.some(r => r.isActive) ? (
													(brandState.currentRetailChannels || [])
														.filter(retail => retail.isActive)
														.map(distributor => (
															<CheckboxFormComponent
																key={'view-retail-' + distributor.id}
																onClick={() => toggleEditing(ID)}
																checkboxType='square'
																name={`currentRetailChannels.view.${distributor.id}`}
																checkboxText={<>{distributor.name}</>}
																value={distributor.isActive}
																disabled={true}
															/>
														))
												) : (
													<CheckboxFormComponent
														checkboxType='square'
														key={'view-retail-none'}
														name={`cleanRetails.view`}
														checkboxText={<div className='description-technique'>None</div>}
														disabled
														value={true}
														onClick={() => toggleEditing(ID)}
													/>
												)}
											</div>
											<div>
												<LabelForms
													icon={<i className='ri-question-line' />}
													toolTipText={<>Your Minimum Advertised Price policy...</>}
													text={DEFAULT_LABELS.mapPolicy}
												/>
												<CheckboxFormComponent
													checkboxType='toggle'
													name='mapPolicy.view'
													value={brandState.mapPolicy!}
													onClick={() => toggleEditing(ID)}
													disabled={true}
												/>
											</div>
										</>
									}
								/>
							) : (
								<>
									<ContainerForms
										FormComponent1={
											<>
												<div>
													<LabelForms text={DEFAULT_LABELS.currentDistributionChannels} />
													{values.currentDistributionChannels!.map((distributor, index) => (
														<CheckboxFormComponent
															key={'edit-distributor-' + distributor.id}
															checkboxType='square'
															name={`currentDistributionChannels[${index}].isActive`}
															checkboxText={<>{distributor.name}</>}
															value={distributor.isActive}
															disabled={false}
															onChange={() => onDistributorChannelChange(distributor.id)}
														/>
													))}
												</div>
												{DISTRIBUTOR_CONFIG.map(
													config =>
														showDistributorRegions[config.id] && (
															<DistributorRegionCheckboxes
																key={`edit-regions-${config.id}`}
																isEditing={true}
																label={DEFAULT_LABELS[config.labelConstant]}
																regionsToDisplay={values[config.regionKey]}
																namePrefix={config.regionKey}
																handleChange={handleChange}
																toggleEditing={toggleEditing}
																formId={ID}
																error={errors[config.regionKey]}
															/>
														)
												)}
											</>
										}
										FormComponent2={
											<>
												<div>
													<LabelForms text={DEFAULT_LABELS.currentRetailChannels} />
													{values.currentRetailChannels!.map((distributor, index) => (
														<CheckboxFormComponent
															key={'edit-retail-' + distributor.id}
															checkboxType='square'
															name={`currentRetailChannels[${index}].isActive`}
															checkboxText={<>{distributor.name}</>}
															value={distributor.isActive}
															disabled={false}
															onChange={handleChange}
														/>
													))}
													<CheckboxFormComponent
														key={'edit-retail-cleanInput'}
														checkboxType='square'
														name={'cleanRetailChannelsAction'}
														checkboxText={<div className=''>None</div>}
														value={!showRetails}
														onChange={cleanRetailInput}
													/>
												</div>
												<div>
													<LabelForms
														icon={<i className='ri-question-line' />}
														toolTipText={<>Your Minimum Advertised Price policy...</>}
														text={DEFAULT_LABELS.mapPolicy}
													/>
													<CheckboxFormComponent
														checkboxType='toggle'
														name='mapPolicy'
														value={values.mapPolicy}
														disabled={false}
														onChange={handleChange}
													/>
												</div>
											</>
										}
									/>
									<ActionsForms
										onUpdate={submitForm}
										onCancel={() => {
											onCancel();
											toggleEditing(null);
											updateKeyFc();
										}}
										isValid={isValid}
										toggleEditing={() => toggleEditing(null)}
									/>
								</>
							))}
					</>
				)}
			</div>
		</div>
	);
};
